import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type { FinancialStatementDto, FinancialStatementFilter, FinancialStatementRow } from '@/types/report.types';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function fmtAmt(n: number) {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

// ── Excel export ───────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Name', header: 'Description', width: 48 },
  { key: 'Amount', header: 'Amount', width: 16, numFmt: '#,##0.00' },
  { key: 'ComparativeAmount', header: 'Comparative Amount', width: 20, numFmt: '#,##0.00' },
  { key: 'RowType', header: 'Row Type', width: 16 },
];

function buildExcelRows(rows: FinancialStatementRow[]) {
  return rows
    .filter(r => r.rowType !== 'SPACER')
    .map(r => ({
      Name: r.name,
      Amount: r.amount,
      ComparativeAmount: r.comparativeAmount ?? '',
      RowType: r.rowType,
    }));
}

// ── Summary tiles ─────────────────────────────────────────────────────────────

function SummaryTile({
  label,
  value,
  highlighted,
}: {
  label: string;
  value: string;
  highlighted?: 'success' | 'error' | null;
}) {
  return (
    <div className={cn(
      'rounded-xl border px-4 py-3',
      highlighted === 'success'
        ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
        : highlighted === 'error'
          ? 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
    )}>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className={cn(
        'text-sm font-semibold tabular-nums',
        highlighted === 'success'
          ? 'text-green-700 dark:text-green-400'
          : highlighted === 'error'
            ? 'text-red-700 dark:text-red-400'
            : 'text-gray-900 dark:text-white'
      )}>
        {value}
      </div>
    </div>
  );
}

// ── Row renderer ───────────────────────────────────────────────────────────────

interface BsRowProps {
  row: FinancialStatementRow;
  showComparative: boolean;
  colSpanTotal: number;
  onDrilldown?: (accountId: string, toDate: string) => void;
  toDate: string;
}

function BsRow({ row, showComparative, colSpanTotal, onDrilldown, toDate }: BsRowProps) {
  const indent = row.indentLevel * 16;

  if (row.rowType === 'SPACER') {
    return <tr><td colSpan={colSpanTotal} className="py-2" /></tr>;
  }

  if (row.rowType === 'VALIDATION') {
    return (
      <tr>
        <td colSpan={colSpanTotal} className="px-4 py-2">
          <div className={cn(
            'flex items-center gap-2 text-xs rounded-lg px-3 py-2',
            row.amount === 0
              ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
              : 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
          )}>
            {row.amount === 0
              ? <CheckCircle2 size={13} />
              : <AlertCircle size={13} />
            }
            {row.name}
            {row.amount !== 0 && (
              <span className="font-semibold ml-1">
                ({fmtAmt(Math.abs(row.amount))} {row.amount > 0 ? 'excess assets' : 'excess liabilities'})
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  }

  if (row.rowType === 'SECTION_HEADER') {
    return (
      <tr className="bg-gray-100 dark:bg-gray-800">
        <td
          colSpan={colSpanTotal}
          className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
      </tr>
    );
  }

  if (row.rowType === 'NET_TOTAL') {
    return (
      <tr className="border-t-2 border-double border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-800/50">
        <td
          className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
        <td className="px-4 py-3 text-sm text-right tabular-nums font-bold text-gray-900 dark:text-white">
          {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
        </td>
        {showComparative && (
          <td className="px-4 py-3 text-sm text-right tabular-nums font-bold text-gray-700 dark:text-gray-300">
            {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
          </td>
        )}
      </tr>
    );
  }

  if (row.rowType === 'SUBTOTAL') {
    return (
      <tr className="border-t border-gray-300 dark:border-gray-600">
        <td
          className="px-4 py-2 text-sm font-semibold text-gray-800 dark:text-gray-200"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
        <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-900 dark:text-white border-t border-gray-300 dark:border-gray-600">
          {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
        </td>
        {showComparative && (
          <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-300 dark:border-gray-600">
            {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
          </td>
        )}
      </tr>
    );
  }

  if (row.rowType === 'CATEGORY') {
    return (
      <tr className="border-t border-gray-100 dark:border-gray-800">
        <td
          colSpan={colSpanTotal}
          className="px-4 py-1.5 text-sm font-semibold italic text-gray-700 dark:text-gray-200"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
      </tr>
    );
  }

  // ACCOUNT row
  return (
    <tr className="border-t border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30 group">
      <td
        className="px-4 py-1.5 text-sm text-gray-700 dark:text-gray-300"
        style={{ paddingLeft: `${indent + 16}px` }}
      >
        <span className="flex items-center gap-1.5">
          {row.code && (
            <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{row.code}</span>
          )}
          {row.name}
          {row.accountId && onDrilldown && (
            <button
              onClick={() => onDrilldown(row.accountId!, toDate)}
              className="opacity-0 group-hover:opacity-100 text-brand-500 hover:text-brand-700 ml-1"
              title="View Account Ledger"
            >
              <ExternalLink size={11} />
            </button>
          )}
        </span>
      </td>
      <td className={cn(
        'px-4 py-1.5 text-sm text-right tabular-nums',
        row.isNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'
      )}>
        {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
      </td>
      {showComparative && (
        <td className={cn(
          'px-4 py-1.5 text-sm text-right tabular-nums',
          row.comparativeAmount != null && row.comparativeAmount < 0
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-600 dark:text-gray-400'
        )}>
          {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
        </td>
      )}
    </tr>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export const BalanceSheetPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<FinancialStatementFilter>({
    toDate: today(),
    showComparative: false,
  });
  const [data, setData] = useState<FinancialStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportApi.getBalanceSheet(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const drilldown = (accountId: string, toDate: string) => {
    const toDateObj = new Date(toDate);
    const fromDate = new Date(toDateObj.getFullYear(), 0, 1).toISOString().split('T')[0];
    navigate(
      `${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${accountId}&fromDate=${fromDate}&toDate=${toDate}`
    );
  };

  if (!hasPermission(Permissions.RPT_BALANCE_SHEET)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const showComparative = filter.showComparative ?? false;
  const colSpanTotal = showComparative ? 3 : 2;
  const isBalanced = data?.isBalanced ?? false;

  // Derive summary figures from named SUBTOTAL rows
  const totalAssets = data?.rows.find(
    r => r.rowType === 'SUBTOTAL' && r.name.toLowerCase().includes('asset')
  )?.amount ?? null;
  const totalLiabilities = data?.rows.find(
    r => r.rowType === 'SUBTOTAL' && r.name.toLowerCase().includes('liabilit')
  )?.amount ?? null;
  const totalEquity = data?.rows.find(
    r => r.rowType === 'SUBTOTAL' && r.name.toLowerCase().includes('equit')
  )?.amount ?? null;

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Balance Sheet',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data.rows),
      title: 'Balance Sheet',
      subtitle: `${data.companyName} · As at ${filter.toDate}`,
    }, `BalanceSheet_${filter.toDate}`);
  };

  const handlePrint = () => setShowPrint(true);

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Balance Sheet</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={handlePrint}
          hasData={!!data}
        />
      </div>

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>As At Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          {showComparative && (
            <div>
              <label className={LABEL_CLS}>Comparative As At</label>
              <input
                type="date"
                value={filter.comparativeToDate ?? ''}
                onChange={e => setFilter(f => ({ ...f, comparativeToDate: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
          )}
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showComparative}
              onChange={e => setFilter(f => ({ ...f, showComparative: e.target.checked }))}
              className="rounded"
            />
            Show Comparative Period
          </label>
          <RunReportButton
            label="Generate Report"
            onClick={run}
            isLoading={loading}
            disabled={!filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Out-of-balance warning banner */}
          {!isBalanced && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl text-sm text-red-700 dark:text-red-400">
              <AlertCircle size={16} />
              <span className="font-medium">Balance Sheet is Out of Balance.</span>
              {data.validationMessage && <span>{data.validationMessage}</span>}
            </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryTile
              label="Total Assets"
              value={totalAssets != null ? fmtAmt(Math.abs(totalAssets)) : '—'}
            />
            <SummaryTile
              label="Total Liabilities"
              value={totalLiabilities != null ? fmtAmt(Math.abs(totalLiabilities)) : '—'}
            />
            <SummaryTile
              label="Total Equity"
              value={totalEquity != null ? fmtAmt(Math.abs(totalEquity)) : '—'}
            />
            <SummaryTile
              label="Balance Status"
              value={isBalanced ? 'Balanced' : 'OUT OF BALANCE'}
              highlighted={isBalanced ? 'success' : 'error'}
            />
          </div>

          {/* Report card */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 flex-1">
            {/* Report header */}
            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
                <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5">{data.title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  As at {fmtDate(data.toDate)} · Generated {fmtDate(data.generatedAt)}
                </div>
              </div>
              <div className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                isBalanced
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              )}>
                {isBalanced ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                {isBalanced ? 'Sheet Balances' : 'Out of Balance'}
              </div>
            </div>

            {/* Print area */}
            <div data-print-section id="report-print-area" className="overflow-auto flex-1">
              {/* Print-only header */}
              <div className="hidden print:block px-4 pt-4 pb-2 text-center">
                <div className="text-lg font-bold">{data.companyName}</div>
                <div className="text-base font-semibold mt-1">Balance Sheet</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">As at {fmtDate(data.toDate)}</div>
              </div>

              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-36">
                      As at {fmtDate(data.toDate)}
                    </th>
                    {showComparative && (
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-36">
                        Comparative
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, idx) => (
                    <BsRow
                      key={`${row.rowType}-${row.code ?? idx}-${idx}`}
                      row={row}
                      showComparative={showComparative}
                      colSpanTotal={colSpanTotal}
                      onDrilldown={drilldown}
                      toDate={filter.toDate}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          Select an "As At" date and click "Generate Report" to view the Balance Sheet.
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Balance Sheet"
          orientation="portrait"
        >
          <ReportHeader
            reportTitle={data.title ?? 'Balance Sheet'}
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'As at',   value: fmtDate(data.toDate) },
              { label: 'Status',  value: data.isBalanced ? 'Balanced ✓' : 'Out of Balance ⚠' },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', marginTop: '8px' }}>
            <thead>
              <tr>
                <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'left', fontWeight: 700, fontSize: '9px' }}>Description</th>
                <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '9px', width: '120px' }}>
                  As at {fmtDate(data.toDate)}
                </th>
                {showComparative && (
                  <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '9px', width: '120px' }}>Comparative</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.rows.filter((r: FinancialStatementRow) => r.rowType !== 'SPACER' && r.rowType !== 'VALIDATION').map((row: FinancialStatementRow, idx: number) => {
                const indent = (row.indentLevel ?? 0) * 12;
                const isHeader   = row.rowType === 'SECTION_HEADER';
                const isCategory = row.rowType === 'CATEGORY';
                const isSubtotal = row.rowType === 'SUBTOTAL';
                const isNetTotal = row.rowType === 'NET_TOTAL';
                const isAccount  = row.rowType === 'ACCOUNT';
                return (
                  <tr key={idx} style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: isHeader ? '#e8edf5' : isNetTotal ? '#f9fafb' : undefined,
                    borderTop: isNetTotal ? '2px solid #374151' : isSubtotal ? '1px solid #d1d5db' : undefined,
                  }}>
                    <td style={{
                      padding: `${isHeader || isNetTotal ? 5 : 3}px 8px`,
                      paddingLeft: `${indent + 8}px`,
                      fontWeight: isHeader || isNetTotal || isSubtotal ? 700 : 400,
                      fontStyle: isCategory ? 'italic' : 'normal',
                      fontSize: isHeader ? '8.5px' : '9px',
                      color: isHeader ? '#1e3a5f' : isAccount ? '#374151' : '#111827',
                      letterSpacing: isHeader ? '0.05em' : undefined,
                      textTransform: isHeader ? 'uppercase' : undefined,
                    }}>
                      {row.code && isAccount && (
                        <span style={{ fontFamily: 'monospace', color: '#9ca3af', marginRight: '6px', fontSize: '8px' }}>{row.code}</span>
                      )}
                      {row.name}
                    </td>
                    {(isAccount || isSubtotal || isNetTotal) ? (
                      <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isNetTotal || isSubtotal ? 700 : 400, color: '#111827', fontSize: '9px' }}>
                        {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
                      </td>
                    ) : <td />}
                    {showComparative && (
                      (isAccount || isSubtotal || isNetTotal) ? (
                        <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: 'monospace', color: '#6b7280', fontSize: '9px' }}>
                          {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
                        </td>
                      ) : <td />
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
